// Single source of truth for the event videos.
//
// The hero slideshow and the video section below it both read this list: the
// hero needs to know which anchor each slide scrolls to, and the video section
// needs to know which videos exist. Keeping one list stops the two drifting
// apart and pointing at anchors that aren't rendered.
//
// `youtubeId` is the part after `v=` in a watch URL, or the last path segment
// of a youtu.be link. An empty id means "no video yet" — that event's block is
// not rendered, and the hero slide falls back to `fallbackAnchor` instead.
//
// NOTE: a YouTube video set to Private cannot be embedded anywhere, even by
// its owner. Unlisted is the setting that works: hidden from search and from
// the channel, but playable and embeddable by anyone with the link.

import heroBg from '../images/wedding/hero-bg.jpg';
import welcomePartyImage from '../images/wedding/welcome-party.jpg';
import genderRevealImage from '../images/baby/gender-reveal.jpg';

export const EVENT_VIDEOS = [
  {
    key: 'bachelorette',
    anchorId: 'video-bachelorette',
    // Recovered from the `backup` branch (commit 3d152b7, "bride-to-be"),
    // which is what production serves — this video was never on `main`.
    youtubeId: 'RWqyYoHXjI4',
    // `image` is the hero slide; the block's poster is YouTube's own still for
    // the video, exactly as the original VideoSection did.
    image: heroBg,
    posterImage: welcomePartyImage,
    useYoutubeThumbnail: true,
    // The video.* keys are the originals from that commit, so this copy is
    // already translated into all four languages.
    titleKey: 'video.title',
    titleDefault: 'Haubuahs Utdrikningslag Overraskelse',
    descriptionKey: 'video.description',
    watchKey: 'video.playText',
    watchDefault: 'Klikk for å se overraskelsesfesten',
    // The video block shows the duration rather than a date, because this
    // evening was not on the wedding day and `hero.date` read as if it were.
    // `dateKey` stays for the hero, whose slide IS the wedding photo.
    metaKey: 'video.duration',
    dateKey: 'hero.date',
    altKey: 'hero.imageAlt',
    // The hero headline and icon shown while this slide is active.
    heroHeadlineKey: 'hero.gettingMarried',
    heroHeadlineDefault: 'Vi Giftet Oss!',
    heroIcon: 'heart',
    // Where the hero slide points while this event has no video of its own.
    fallbackAnchor: 'wedding-album'
  },
  {
    key: 'babyReveal',
    anchorId: 'video-baby-reveal',
    youtubeId: 'lDBkVthSoa8',
    image: genderRevealImage,
    objectPosition: 'center 62%',
    titleKey: 'babyReveal.title',
    titleDefault: 'Det ble en jente!',
    dateKey: 'babyReveal.date',
    altKey: 'hero.slideRevealAlt',
    heroHeadlineKey: 'hero.littleGirl',
    heroHeadlineDefault: 'Vår Lille Jente',
    heroIcon: 'baby',
    fallbackAnchor: null
  }
];

/**
 * Whether this event gets a block in the video section: it has a video, or a
 * story to tell while its video is still being supplied.
 */
export const hasBlock = event => Boolean(event.youtubeId || event.descriptionKey);

/** The events rendered in the video section, in order. */
export const videosWithContent = () => EVENT_VIDEOS.filter(hasBlock);

/**
 * Where a hero slide for this event should scroll to: its own block if one is
 * rendered, otherwise its declared fallback, otherwise nowhere.
 */
export const scrollTargetFor = event =>
  hasBlock(event) ? event.anchorId : event.fallbackAnchor;
